import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Target,
  Building2,
  Calendar,
  DollarSign,
  MoreVertical,
  Pencil,
  Trash2,
  ArrowRight,
  MapPin,
  BrainCircuit,
} from "lucide-react";

// Sortable Opportunity Card
const SortableOpportunityCard = ({ opportunity, stages, onEdit, onArchive, onConvert, onPPA, formatCurrency }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: opportunity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const stage = stages?.find(s => s.id === opportunity.stage);
  const isClosed = stage?.is_closed;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card className={`bg-card/80 border-border/50 hover:border-primary/30 transition-all cursor-grab active:cursor-grabbing ${isDragging ? 'shadow-lg' : ''}`}>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm truncate">{opportunity.name}</h4>
              {opportunity.client_company_name && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{opportunity.client_company_name}</span>
                </p>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(opportunity)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                {onPPA && (
                  <DropdownMenuItem onClick={() => onPPA(opportunity)}>
                    <BrainCircuit className="mr-2 h-4 w-4" />
                    PPA Intelligence
                  </DropdownMenuItem>
                )}
                {!opportunity.converted_project_id && !isClosed && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onConvert(opportunity)} className="text-green-400">
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Convert to Project
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onArchive(opportunity)} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {opportunity.city && opportunity.state && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {opportunity.city}, {opportunity.state}
            </p>
          )}

          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-primary">
              {formatCurrency(opportunity.estimated_value)}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">{opportunity.probability_percent}%</span>
              <Progress value={opportunity.probability_percent} className="w-8 h-1.5" />
            </div>
          </div>

          {opportunity.due_date && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              Due: {new Date(opportunity.due_date).toLocaleDateString()}
            </div>
          )}

          {opportunity.converted_project_id && (
            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
              Converted
            </Badge>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Droppable Column
const StageColumn = ({ stage, opportunities, children, totalValue }) => {
  const formatValue = (value) => {
    if (!value) return "$0";
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  return (
    <div className="flex flex-col min-w-[280px] max-w-[280px]">
      {/* Column Header */}
      <div 
        className="p-3 rounded-t-lg border-b-2"
        style={{ 
          backgroundColor: `${stage.color}10`,
          borderColor: stage.color 
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: stage.color }}
            />
            <span className="font-medium text-sm">{stage.name}</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {opportunities.length}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {formatValue(totalValue)}
        </div>
      </div>

      {/* Column Content */}
      <div 
        className="flex-1 p-2 space-y-2 min-h-[400px] rounded-b-lg border border-t-0 border-border/30 bg-muted/10"
      >
        <SortableContext items={opportunities.map(o => o.id)} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        
        {opportunities.length === 0 && (
          <div className="text-center py-8 text-muted-foreground/50 text-sm">
            No opportunities
          </div>
        )}
      </div>
    </div>
  );
};

// Overlay card when dragging
const DragOverlayCard = ({ opportunity, formatCurrency }) => {
  return (
    <Card className="bg-card border-primary shadow-xl w-[264px]">
      <CardContent className="p-3 space-y-2">
        <h4 className="font-medium text-sm">{opportunity.name}</h4>
        {opportunity.client_company_name && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Building2 className="h-3 w-3" />
            {opportunity.client_company_name}
          </p>
        )}
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-primary">
            {formatCurrency(opportunity.estimated_value)}
          </span>
          <span className="text-muted-foreground">{opportunity.probability_percent}%</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Kanban Board Component
const OpportunityKanban = ({
  opportunities,
  pipeline,
  onStageChange,
  onEdit,
  onArchive,
  onConvert,
  onPPA,
}) => {
  const [activeId, setActiveId] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      maximumFractionDigits: 0 
    }).format(value);
  };

  // Get active stages only (not closed)
  const activeStages = pipeline?.stages?.filter(s => !s.is_closed) || [];
  const closedStages = pipeline?.stages?.filter(s => s.is_closed) || [];

  // Group opportunities by stage
  const opportunitiesByStage = {};
  activeStages.forEach(stage => {
    opportunitiesByStage[stage.id] = opportunities.filter(o => o.stage === stage.id);
  });
  closedStages.forEach(stage => {
    opportunitiesByStage[stage.id] = opportunities.filter(o => o.stage === stage.id);
  });

  const activeOpportunity = activeId ? opportunities.find(o => o.id === activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeOpp = opportunities.find(o => o.id === active.id);
    if (!activeOpp) return;

    // Find which stage the item was dropped into
    let newStage = null;

    // Check if dropped on another opportunity
    const overOpp = opportunities.find(o => o.id === over.id);
    if (overOpp) {
      newStage = overOpp.stage;
    } else {
      // Dropped on a stage column directly
      newStage = over.id;
    }

    // Only update if stage changed
    if (newStage && newStage !== activeOpp.stage) {
      const stage = pipeline?.stages?.find(s => s.id === newStage);
      if (stage) {
        onStageChange(activeOpp, newStage, stage.probability);
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeOpp = opportunities.find(o => o.id === active.id);
    const overOpp = opportunities.find(o => o.id === over.id);

    if (!activeOpp) return;

    // If we're over an opportunity in a different stage
    if (overOpp && activeOpp.stage !== overOpp.stage) {
      // We'll handle the actual move in dragEnd
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {/* Active Stages */}
        {activeStages.map(stage => {
          const stageOpps = opportunitiesByStage[stage.id] || [];
          const totalValue = stageOpps.reduce((sum, o) => sum + (o.estimated_value || 0), 0);
          
          return (
            <StageColumn 
              key={stage.id} 
              stage={stage} 
              opportunities={stageOpps}
              totalValue={totalValue}
            >
              {stageOpps.map(opp => (
                <SortableOpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  stages={pipeline?.stages}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  onConvert={onConvert}
                  onPPA={onPPA}
                  formatCurrency={formatCurrency}
                />
              ))}
            </StageColumn>
          );
        })}

        {/* Closed Stages (Won/Lost) - smaller columns */}
        {closedStages.map(stage => {
          const stageOpps = opportunitiesByStage[stage.id] || [];
          const totalValue = stageOpps.reduce((sum, o) => sum + (o.estimated_value || 0), 0);
          
          return (
            <StageColumn 
              key={stage.id} 
              stage={stage} 
              opportunities={stageOpps}
              totalValue={totalValue}
            >
              {stageOpps.map(opp => (
                <SortableOpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  stages={pipeline?.stages}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  onConvert={onConvert}
                  onPPA={onPPA}
                  formatCurrency={formatCurrency}
                />
              ))}
            </StageColumn>
          );
        })}
      </div>

      <DragOverlay>
        {activeOpportunity ? (
          <DragOverlayCard opportunity={activeOpportunity} formatCurrency={formatCurrency} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default OpportunityKanban;
